import { useState, useRef, useLayoutEffect, ChangeEvent } from "react";
import "./StudentPhoto.css";
import magnifyPlus from "../../../images/magnifyingGlassPlus.svg";
import magnifyMinus from "../../../images/magnifyingGlassMinus.svg";
import fillMagnifyPlus from "../../../images/fillMagnifyingGlassPlus.svg";
import fillMagnifyMinus from "../../../images/fillMagnifyingGlassMinus.svg";
import { sleep } from "../../../../helpers/util";
import { CoreButton, CoreModal } from "@verity/components/core";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import ZoomOutRoundedIcon from "@mui/icons-material/ZoomOutRounded";
import i18n from "../../../../i18n";
import apiClient from "../../../../helpers/api-client";

export const StudentPhoto = (props: { type: string; sessionUuid: string }) => {
	const [infoSrc, setInfoSrc] = useState<string | null>(null);
	const [displayStatus, setDisplayStatus] = useState(true);

	const maxZoom = 500;
	const minZoom = 25;
	const zoomStep = 25;

	useLayoutEffect(() => {
		// Assume image has extension .png
		if (props.sessionUuid) {
			// Fetched from this URL as this is the S3 format we store it in.
			apiClient.proctoringSession
				.getPhotoUrl(props.sessionUuid, props.type === "studentId" ? "id" : "person")
				.then((photoUrl) => {
					setInfoSrc(photoUrl ?? null);
					setDisplayStatus(true);
				});
		}
	}, [props.sessionUuid, props.type]);

	let lastZoom = 100;
	let currentZoom = 100;
	let fakeOneSeventyFive = false;
	let fakeThreeFifty = false;
	const [newCurrentZoom, setNewCurrentZoom] = useState(100);
	const [inputCurrentZoom, setInputCurrentZoom] = useState(100);

	// This is needed to ensure that there isn't a weirdly strong amount of movement when
	// dragging the photo for zooms that are multiples of 175
	function checkOneSeventyFiveMult() {
		if (currentZoom == 175) {
			currentZoom = 176;
			fakeOneSeventyFive = true;
			fakeThreeFifty = false;
		} else if (currentZoom == 350) {
			currentZoom = 351;
			fakeThreeFifty = true;
			fakeOneSeventyFive = false;
		} else {
			fakeOneSeventyFive = false;
			fakeThreeFifty = false;
		}
	}

	function fixOneSeventyFiveMult() {
		if (fakeOneSeventyFive) {
			currentZoom = 175;
		} else if (fakeThreeFifty) {
			currentZoom = 350;
		}
	}

	const canvasRef = useRef<HTMLCanvasElement>(null);

	function makeCanvas() {
		const canvas = canvasRef.current; //document.getElementById(`${props.type}_${props.sessionUuid}_canvas`) as HTMLCanvasElement | null;
		let savedTransforms: DOMMatrix[] = [];

		if (canvas) {
			const gkhead = new Image();
			gkhead.src = infoSrc ?? "";

			canvas.width = gkhead.width;
			canvas.height = gkhead.height;

			const ctx = canvas.getContext("2d") as
				| (CanvasRenderingContext2D & {
						transformedPoint: (x: number, y: number) => DOMPoint;
						transformedPointRedraw: (x: number, y: number) => DOMPoint;
				  })
				| null;
			if (ctx) {
				trackTransforms(ctx);

				const redraw = () => {
					const pt = ctx.transformedPointRedraw(canvas.width / 2, canvas.height / 2);
					ctx.translate(pt.x, pt.y);
					ctx.scale(1 / (lastZoom / 100), 1 / (lastZoom / 100));
					ctx.scale(currentZoom / 100, currentZoom / 100);
					ctx.translate(-pt.x, -pt.y);
					lastZoom = currentZoom;

					ctx.save();
					ctx.setTransform(1, 0, 0, 1, 0, 0);
					ctx.clearRect(0, 0, canvas.width, canvas.height);
					ctx.restore();

					ctx.drawImage(gkhead, 0, 0);
				};

				let lastX = canvas.width / 2,
					lastY = canvas.height / 2;

				let dragStart: { x: number; y: number } | null;
				let dragged: boolean;

				let times_in_one_second = 0;

				// This function and this timer is to ensure that if people try to zoom in by pinching their fingers on a trackpad that
				//  the zoom stays relatively slow
				const reset_times = async () => {
					await sleep(200);
					times_in_one_second = 0;
				};

				const handleScroll = function (evt: Event & { wheelDelta?: number; detail?: number }) {
					if (times_in_one_second == 0) {
						times_in_one_second++;
						void reset_times();
					} else if (times_in_one_second > 4) {
						evt.preventDefault();
						return false;
					} else {
						times_in_one_second++;
					}
					const delta = evt.wheelDelta ? (evt.wheelDelta > 0 ? 2 : -2) : evt.detail ? -evt.detail : 0;
					if (delta > 0) {
						if (currentZoom >= maxZoom) {
							evt.preventDefault();
							return false;
						}
						fixOneSeventyFiveMult();
						currentZoom += zoomStep;
						currentZoom >= maxZoom ? (currentZoom = maxZoom) : null;
						setNewCurrentZoom(currentZoom);
						setInputCurrentZoom(currentZoom);
						checkOneSeventyFiveMult();
						redraw();
					} else if (delta < 0) {
						if (currentZoom <= minZoom) {
							evt.preventDefault();
							return false;
						}
						fixOneSeventyFiveMult();
						currentZoom -= zoomStep;
						currentZoom <= minZoom ? (currentZoom = minZoom) : null;
						setNewCurrentZoom(currentZoom);
						setInputCurrentZoom(currentZoom);

						checkOneSeventyFiveMult();

						redraw();
					}
					// console.log(currentZoom);
					evt.preventDefault();
					return false;
				};

				const change_zoom_icon = async (isZoomIn: boolean) => {
					if (!isZoomIn) {
						const zoom_button = document.getElementById(
							`magnify_further_image_${props.type}_${props.sessionUuid}`,
						) as HTMLImageElement | null;
						if (zoom_button) {
							zoom_button.src = fillMagnifyMinus;
							await sleep(100);
							zoom_button.src = magnifyMinus;
						}
					} else {
						const zoom_button = document.getElementById(
							`magnify_closer_image_${props.type}_${props.sessionUuid}`,
						) as HTMLImageElement | null;
						if (zoom_button) {
							zoom_button.src = fillMagnifyPlus;
							await sleep(100);
							zoom_button.src = magnifyPlus;
						}
					}
				};

				const zoom_in = () => {
					if (currentZoom >= maxZoom) {
						return;
					}
					void change_zoom_icon(true);
					fixOneSeventyFiveMult();
					currentZoom += zoomStep;
					currentZoom >= maxZoom ? (currentZoom = maxZoom) : null;
					// console.log(currentZoom);
					setNewCurrentZoom(currentZoom);
					setInputCurrentZoom(currentZoom);
					checkOneSeventyFiveMult();
					redraw();
				};

				const zoom_out = () => {
					if (currentZoom <= minZoom) {
						return;
					}
					void change_zoom_icon(false);
					// console.log(currentZoom);
					fixOneSeventyFiveMult();
					currentZoom -= zoomStep;
					currentZoom <= minZoom ? (currentZoom = minZoom) : null;
					setNewCurrentZoom(currentZoom);
					setInputCurrentZoom(currentZoom);
					checkOneSeventyFiveMult();
					redraw();
				};

				const resetZoomState = () => {
					currentZoom = 100;
					times_in_one_second = 0;
					redraw();
				};

				const inputChange = (changed: number) => {
					currentZoom = changed;
					checkOneSeventyFiveMult();
					redraw();
					// console.log(currentZoom);
				};

				canvas.addEventListener(
					"mousedown",
					(evt) => {
						// document.body.style.mozUserSelect =
						// document.body.style.webkitUserSelect =
						document.body.style.userSelect = "none";
						lastX = evt.offsetX || evt.pageX - canvas.offsetLeft;
						lastY = evt.offsetY || evt.pageY - canvas.offsetTop;
						dragStart = ctx.transformedPoint(lastX, lastY);
						dragged = false;
						dragged;
					},
					false,
				);

				canvas.addEventListener(
					"mousemove",
					(evt) => {
						lastX = evt.offsetX || evt.pageX - canvas.offsetLeft;
						lastY = evt.offsetY || evt.pageY - canvas.offsetTop;
						dragged = true;
						if (dragStart) {
							let dragScale = 100 / currentZoom;
							dragScale *= 2;
							if (dragScale >= 1) dragScale = 1;
							const pt = ctx.transformedPoint(lastX, lastY);
							ctx.translate((pt.x - dragStart.x) * dragScale, (pt.y - dragStart.y) * dragScale);
							redraw();
						}
					},
					false,
				);

				canvas.addEventListener(
					"mouseup",
					function () {
						dragStart = null;
					},
					false,
				);

				canvas.addEventListener("DOMMouseScroll", handleScroll, false);
				canvas.addEventListener("mousewheel", handleScroll, false);

				document
					.getElementById(`magnify_closer_${props.type}_${props.sessionUuid}`)
					?.addEventListener("click", zoom_in);
				document
					.getElementById(`magnify_further_${props.type}_${props.sessionUuid}`)
					?.addEventListener("click", zoom_out);

				document
					?.getElementById(`reset_${props.type}_${props.sessionUuid}_button`)
					?.addEventListener("click", resetZoomState);
				document.getElementById(`${props.type}_${props.sessionUuid}_icon`)?.addEventListener("click", resetZoomState);

				document
					?.querySelector(`#magnifying_slider_${props.type}_${props.sessionUuid}`)
					?.addEventListener("change", (event) => {
						const changed = parseInt((event.target as HTMLInputElement).value);
						inputChange(changed);
					});

				document
					?.querySelector(`#magnifying_input_${props.type}_${props.sessionUuid}`)
					?.addEventListener("change", (event) => {
						const changed = parseInt((event.target as HTMLInputElement).value);
						if (changed > maxZoom || changed < minZoom) {
							return;
						} else {
							inputChange(changed);
						}
					});

				redraw();
			}
		}

		function trackTransforms(ctx: any) {
			let xform = new DOMMatrix();
			ctx.getTransform = function () {
				return xform;
			};

			savedTransforms = [];

			const save = ctx.save;
			ctx.save = function () {
				savedTransforms.push(xform.translate(0, 0));
				// eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
				return save.call(ctx);
			};

			const restore = ctx.restore;
			ctx.restore = function () {
				const xformTemp = savedTransforms.pop();
				if (xformTemp) {
					xform = xformTemp;
				}
				// eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
				return restore.call(ctx);
			};

			const scale = ctx.scale;
			ctx.scale = function (sx: number, sy: number) {
				xform = xform.scaleSelf(sx, sy);
				// eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
				return scale.call(ctx, sx, sy);
			};

			const translate = ctx.translate;
			ctx.translate = function (dx: number, dy: number) {
				xform = xform.translate(dx, dy);
				// eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
				return translate.call(ctx, dx, dy);
			};

			const transform = ctx.transform;
			ctx.transform = function (a: number, b: number, c: number, d: number, e: number, f: number) {
				const m2 = new DOMMatrix();
				m2.a = a;
				m2.b = b;
				m2.c = c;
				m2.d = d;
				m2.e = e;
				m2.f = f;
				xform = xform.multiply(m2);
				// eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
				return transform.call(ctx, a, b, c, d, e, f);
			};

			const setTransform = ctx.setTransform;
			ctx.setTransform = function (a: number, b: number, c: number, d: number, e: number, f: number) {
				xform.a = a;
				xform.b = b;
				xform.c = c;
				xform.d = d;
				xform.e = e;
				xform.f = f;
				// eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
				return setTransform.call(ctx, a, b, c, d, e, f);
			};

			ctx.transformedPoint = function (x: number, y: number) {
				const pt = new DOMPoint(x, y);
				try {
					return pt.matrixTransform(xform.inverse());
				} catch {
					return pt.matrixTransform();
				}
			};

			ctx.transformedPointRedraw = function (x: number, y: number) {
				const pt = new DOMPoint(x, y);
				return pt.matrixTransform();
			};
		}
	}

	const [showExpanded, setShowExpanded] = useState(false);

	async function expandID() {
		setShowExpanded(true);
		await sleep(100);
		makeCanvas();
		await sleep(100);
		document
			?.getElementById(`expanded_${props.type}_${props.sessionUuid}`)
			?.scrollIntoView({ behavior: "smooth", inline: "start", block: "start" });
	}

	function closeExpand() {
		setShowExpanded(false);
		setNewCurrentZoom(100);
		setInputCurrentZoom(100);
	}

	function resetCanvas() {
		makeCanvas();
		setNewCurrentZoom(100);
		setInputCurrentZoom(100);
	}

	function handleChange(event: ChangeEvent<HTMLInputElement>) {
		const changed = parseInt(event.target.value);
		setNewCurrentZoom(changed);
		setInputCurrentZoom(changed);
		currentZoom = changed;
	}

	function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
		const value = event.target.value;
		if (!value) setInputCurrentZoom(100);
		const changed = parseInt(value);
		// console.log(changed);
		if (changed == inputCurrentZoom) return;
		if (changed > maxZoom || changed < minZoom) {
			setInputCurrentZoom(changed);
		} else {
			setNewCurrentZoom(changed);
			setInputCurrentZoom(changed);
		}
	}

	return (
		<div
			className="student_info_wrapper"
			style={displayStatus && infoSrc ? { display: "inline-block" } : { display: "none" }}
		>
			<CoreModal
				open={showExpanded}
				onClose={closeExpand}
				title={
					props.type === "studentId"
						? i18n.t("result.components.studentSelection.studentInfo.studentPhotoTSX.idDetail")
						: i18n.t("result.components.studentSelection.studentInfo.studentPhotoTSX.photoDetail")
				}
				buttons={
					<CoreButton
						id={`reset_${props.type}_${props.sessionUuid}_button`}
						onClick={resetCanvas}
						key={"reset_canvas_button"}
					>
						{i18n.t("result.components.studentSelection.studentInfo.studentPhotoTSX.reset")}
					</CoreButton>
				}
				showBottomCloseButton
			>
				<div className={`expanded_${props.type}`} id={`expanded_${props.type}_${props.sessionUuid}`}>
					<canvas
						className="expanded_image"
						id={`${props.type}_${props.sessionUuid}_canvas`}
						ref={canvasRef}
						// alt={props.type === "studentId" ? "Photo of Candidates's ID Expanded" : "Photo taken of Candidate Expanded"}
					></canvas>
					<div className="instructions_expanded">
						<p className="instructions_p">
							{i18n.t("result.components.studentSelection.studentInfo.studentPhotoTSX.scrollZoom")}
						</p>
					</div>
					<div className="magnifying_holder">
						<CoreButton
							className="magnifying_further_button"
							id={`magnify_further_${props.type}_${props.sessionUuid}`}
							color={"secondary"}
							style={{ padding: "0", height: "30px", minWidth: "40px" }}
							disabled={newCurrentZoom <= minZoom}
						>
							<ZoomOutRoundedIcon />
						</CoreButton>
						<input
							className="magnifying_slider"
							id={`magnifying_slider_${props.type}_${props.sessionUuid}`}
							type="range"
							min={minZoom}
							max={maxZoom}
							value={newCurrentZoom}
							onChange={handleChange}
						></input>
						<CoreButton
							className="magnifying_closer_button"
							id={`magnify_closer_${props.type}_${props.sessionUuid}`}
							color={"secondary"}
							style={{ padding: "0", height: "30px", minWidth: "40px" }}
							disabled={newCurrentZoom >= maxZoom}
						>
							<ZoomInRoundedIcon />
						</CoreButton>
						<input
							type="number"
							min={minZoom}
							max={maxZoom}
							step={zoomStep}
							value={inputCurrentZoom}
							className="magnifying_input"
							id={`magnifying_input_${props.type}_${props.sessionUuid}`}
							onChange={handleInputChange}
						></input>
					</div>
				</div>
			</CoreModal>
			<figure className="student_info">
				<figcaption style={{ marginBottom: "10px" }}>
					{props.type === "studentId"
						? i18n.t("result.components.studentSelection.studentInfo.studentPhotoTSX.candidateId")
						: i18n.t("result.components.studentSelection.studentInfo.studentPhotoTSX.candidatePhoto")}
				</figcaption>
				<button
					className="svg-player-icon-holder"
					id={`${props.type}_${props.sessionUuid}_icon`}
					onClick={() => void expandID()}
				>
					<svg
						version="1.1"
						className="svg-player-icon-fullscreen"
						xmlns="http://www.w3.org/2000/svg"
						xmlnsXlink="http://www.w3.org/1999/xlink"
						x="0px"
						y="0px"
						width="24px"
						height="24px"
						viewBox="0 0 24 24"
						enableBackground="new 0 0 24 24"
						xmlSpace="preserve"
					>
						<g>
							<path
								d="M23,0h-6.5c-0.553,0-1,0.448-1,1c0,0.553,0.447,1,1,1H22v3.5c0,0.553,0.447,1,1,1c0.552,0,1-0.447,1-1V1
                                C24,0.448,23.552,0,23,0z"
							/>
							<path
								d="M1,6.5c0.552,0,1-0.447,1-1V2h5.5c0.552,0,1-0.447,1-1c0-0.552-0.448-1-1-1H1C0.447,0,0,0.448,0,1v4.5
                                C0,6.053,0.447,6.5,1,6.5z"
							/>
							<path
								d="M23,17c-0.553,0-1,0.448-1,1v4h-6c-0.553,0-1,0.448-1,1c0,0.553,0.447,1,1,1h7c0.552,0,1-0.447,1-1v-5
                                C24,17.448,23.552,17,23,17z"
							/>
							<path
								d="M8,22H2v-4c0-0.552-0.448-1-1-1c-0.553,0-1,0.448-1,1v5c0,0.553,0.447,1,1,1h7c0.552,0,1-0.447,1-1C9,22.448,8.552,22,8,22
                                z"
							/>
						</g>
					</svg>
				</button>
				<img
					className="info_image"
					onError={() => setDisplayStatus(false)}
					src={infoSrc ?? ""}
					alt={props.type === "studentId" ? "Photo of Candidates's ID" : "Photo taken of Candidate"}
				/>
			</figure>
		</div>
	);
};

export default StudentPhoto;
